// src/hooks/useQuoteView.js
// SEAM 10 VERIFIER: qview-6b2d4e91-hook
//
// When the CUSTOMER viewed an estimate in the portal:
//   organizations/{orgId}/quoteViews/{quoteId}
//     firstViewedAt, lastViewedAt, viewCount, firstViewedSentAt, lastViewedSentAt
// Written only by the recordQuoteView Cloud Function
// (functions/utils/quoteView.js). Staff-read; portal customers cannot read it
// (firestore.rules qview-6b2d4e91-rules), so these hooks must never be used in
// a portal view.
//
// viewCount counts VISITS, not page loads: the server counts a view only if
// the last one was 30+ minutes earlier (or belongs to a newer send).
//
//   useQuoteView(orgId, quoteId) → { record, loaded }   the estimate page
//   useQuoteViews(orgId)         → Map quoteId → record  the list + board (ONE
//                                  listener for the whole org, attached to the
//                                  rows in QuotesView like `attention`)
import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatRelativeTime, toJsDate } from '../utils/formatters';

export default function useQuoteView(orgId, quoteId) {
    const [state, setState] = useState({ record: null, loaded: false });

    useEffect(() => {
        if (!orgId || !quoteId) { setState({ record: null, loaded: false }); return undefined; }
        setState({ record: null, loaded: false });
        return onSnapshot(
            doc(db, `organizations/${orgId}/quoteViews`, quoteId),
            (snap) => setState({ record: snap.exists() ? { id: snap.id, ...snap.data() } : null, loaded: true }),
            (err) => {
                console.warn('useQuoteView: subscription failed', err);
                setState({ record: null, loaded: true });
            }
        );
    }, [orgId, quoteId]);

    return state;
}

export function useQuoteViews(orgId) {
    const [byId, setById] = useState(() => new Map());

    useEffect(() => {
        if (!orgId) { setById(new Map()); return undefined; }
        return onSnapshot(
            collection(db, `organizations/${orgId}/quoteViews`),
            (snap) => setById(new Map(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]))),
            (err) => {
                // Optional data: the list and board still work without it.
                console.warn('useQuoteViews: subscription failed', err);
                setById(new Map());
            }
        );
    }, [orgId]);

    return byId;
}

// "first Oct 1, 2:14 PM" — the absolute moment, for the estimate page.
function formatMoment(value) {
    const d = toJsDate(value);
    if (!d) return null;
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// The ONE wording for a view record, so the estimate page, the board card and
// the list cannot disagree. null when there is nothing to say.
//   full:  "Viewed by customer · first Oct 1, 2:14 PM · last 3 hours ago · 4 views"
//   short: "Viewed 3 hours ago"   (board card + list)
export function quoteViewSummary(record) {
    if (!record || !Number.isFinite(record.viewCount) || record.viewCount < 1) return null;
    const first = formatMoment(record.firstViewedAt);
    const last = formatRelativeTime(record.lastViewedAt);
    const count = `${record.viewCount} view${record.viewCount === 1 ? '' : 's'}`;
    const parts = ['Viewed by customer'];
    if (first) parts.push(`first ${first}`);
    if (last && record.viewCount > 1) parts.push(`last ${last}`);
    parts.push(count);
    return {
        full: parts.join(' · '),
        short: last ? `Viewed ${last}` : 'Viewed',
        count: record.viewCount,
    };
}
